import { memo } from "react";
import { asFullDateTime } from "../../../utils/date";
import { formatReportingPeriod } from "./periodLabel";

/**
 * Audit stamp for the Ongoing Reporting tab (#4800). Unlike the plain {@link FieldStamp},
 * this shows the reporting PERIOD the displayed value was last updated for — derived from
 * the field's carry-forward source (`asOfDate` + `periodType`). So when an analyst views a
 * later period (e.g. Q3 2025) whose value carried forward from an earlier edit, they see
 * "Last updated for Q1 2025" rather than just a wall-clock timestamp. View-mode only.
 */
export const FieldPeriodStamp = memo(function FieldPeriodStamp({
  field,
  className,
}: {
  field: {
    modifyDate?: Date | string;
    modifyUser?: string;
    asOfDate?: Date | string | null;
    periodType?: string | null;
    ownerId?: string | number;
    ownerType?: string;
    fieldKey?: string;
  } | null;
  className?: string;
}): JSX.Element {
  const hasData =
    !!field && !!field.fieldKey && !!field.ownerId && !!field.ownerType;

  const classes = className ?? "in-secondary-blue font-12 mr-10";

  const sourcePeriod = formatReportingPeriod(field?.asOfDate, field?.periodType);

  if (field?.modifyDate && field.modifyUser) {
    return (
      <div data-cy="field-period-timestamp" className={classes}>
        {sourcePeriod ? `Last updated for ${sourcePeriod} — ` : "Updated by "}
        {`${field.modifyUser}, ${asFullDateTime(field.modifyDate as Date)}`}
      </div>
    );
  }

  return (
    <div data-cy="field-period-timestamp-not_edited" className={classes}>
      {hasData ? "Not edited yet" : null}
    </div>
  );
});
